import React, { forwardRef } from 'react';
import { LucideIcon, Check } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: LucideIcon;
  helperText?: string;
  success?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, icon: Icon, helperText, success, className = '', ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          {Icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </div>
          )}
          <input
            ref={ref}
            className={`
              w-full rounded-lg border-2 transition-colors duration-200
              ${Icon ? 'pl-9 sm:pl-10' : 'pl-3 sm:pl-4'} ${success && !error ? 'pr-10 sm:pr-11' : 'pr-3 sm:pr-4'} py-2 sm:py-2.5
              text-sm sm:text-base
              ${error
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : success
                ? 'border-green-300 focus:border-green-500 focus:ring-green-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
              }
              focus:outline-none focus:ring-4
              disabled:bg-gray-50 disabled:text-gray-500
              min-h-[40px] sm:min-h-[44px]
              ${className}
            `}
            {...props}
          />
          {success && !error && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Check className="h-5 w-5 text-green-600" />
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600 flex items-start">
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="break-words">{error}</span>
          </p>
        )}
        {helperText && !error && (
          <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
